// This file configures the initialization of Sentry on the client.
// The config you add here will be used whenever a users loads a page in their browser.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/

import * as Sentry from "@sentry/nextjs";
import {
  shouldDropHandledMicPermissionError,
  shouldDropLogtailFailedToFetch,
} from "./lib/sentry/filters";
import {
  redactContentTokenFromBreadcrumb,
  redactSensitiveDataFromEvent,
  redactSensitiveDataFromSpan,
} from "./lib/sentry/redact-content-token";
import { scrubPhiFromBreadcrumb, scrubPhiFromEvent, scrubPhiFromLog } from "./lib/sentry/scrub-phi";
import { shouldInitSentry } from "./lib/sentry/should-init-sentry";

shouldInitSentry() &&
  Sentry.init({
    dsn: "https://74358742a63b3e0490f4a9b04a84c2bb@o4508682504110080.ingest.us.sentry.io/4508682505224197",

    // Tag events with the app's own environment so deployed events are attributed
    // correctly and never silently default to "production".
    environment: process.env.NEXT_PUBLIC_VIBRANT_ENV,

    // Enable structured logging via Sentry.logger
    enableLogs: true,

    // Add optional integrations for additional features
    integrations: [
      // PHI-safe: mask all text and block media in replay — clinical EHR displays patient data
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
      Sentry.captureConsoleIntegration({
        levels: ["error"],
      }),
    ],

    // Define how likely traces are sampled. Adjust this value in production, or use tracesSampler for greater control.
    tracesSampleRate: 1,

    // Define how likely Replay events are sampled.
    // This sets the sample rate to be 10%. You may want this to be 100% while
    // in development and sample at a lower rate in production
    replaysSessionSampleRate: 0.1,

    // Define how likely Replay events are sampled when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    // Skip automatic OpenTelemetry setup to prevent conflicts with Langfuse
    skipOpenTelemetrySetup: true,

    // Don't register ESM loader hooks as we'll handle this manually
    registerEsmLoaderHooks: false,

    // Filter benign Logtail flush cancellations captured via console.error, then scrub.
    beforeSend(event, hint) {
      if (shouldDropLogtailFailedToFetch({ event, hint })) return null;
      if (shouldDropHandledMicPermissionError({ event })) return null;
      return scrubPhiFromEvent(redactSensitiveDataFromEvent(event));
    },
    // `beforeSend` covers ERROR events only, and `tracesSampleRate` above is 1 — so without
    // this hook every ordinary successful request publishes its URL on the transaction's trace
    // context and HTTP spans, with nothing having failed.
    //
    // The content-token scrub only, NOT the composed `redact-secret-urls` pass: the drain's
    // heartbeat URL is server-side and its module pulls the logger, which has no business in
    // the browser bundle.
    beforeSendTransaction: redactSensitiveDataFromEvent,
    // A child span can leave through the span envelope instead of the transaction exporter, and
    // `beforeSendTransaction` never sees that one. AI SDK spans carry the LLM prompt, which
    // renders the patient name and date of birth (ENG-5590).
    beforeSendSpan: redactSensitiveDataFromSpan,
    beforeBreadcrumb: (breadcrumb) =>
      scrubPhiFromBreadcrumb(redactContentTokenFromBreadcrumb(breadcrumb)),
    // The structured log stream has its own hook. `logger.*` mirrors every payload into
    // `Sentry.logger`, and no other hook sees it.
    beforeSendLog: scrubPhiFromLog,
  });
